import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sha256 } from "js-sha256";

import {
  clearReportState,
  getGeneratorData,
  getMembersList,
  setReportId,
  setReportLoading,
} from "../../redux";
import ReportTemplate from "./components/ReportTemplate/ReportTemplate";
import ReportToolbar from "./components/ReportToolbar/ReportToolbar";
import { ReportTopBar } from "./components/ReportTopBar/ReportTopBar";
import { formatOptions } from "./utils/sizingData";
import { loadAllTemplateImages } from "../generator-data/functions/loadAllTemplateImages";
import { getReportSettingByKey } from "./utils/reportFunctions";

import "./ReportGenerator.scss";

export function ReportGenerator() {
  const generatorRef = useRef();

  const [format] = useState(formatOptions[0]);
  const [coverImage, setCoverImage] = useState({});
  const [pages, setPages] = useState([]);
  const [templateApplied, setTemplateApplied] = useState(true);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [downloadingAdditionalData, setDownloadingAdditionalData] =
    useState(true);

  const dispatch = useDispatch();

  const {
    mainReducer: { activeWorkspaceUuid, notifications },
    reportReducer: { layout, gridElements, downloadingData },
  } = useSelector((state) => state);

  const loadAllImages = () => {
    loadAllTemplateImages(".report-template", setImagesLoaded);
  };

  const generateReportId = () => {
    const newReportId = sha256(getReportSettingByKey("ids")?.join("_"));
    dispatch(setReportId(newReportId));
  };

  useEffect(() => {
    generateReportId();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(getGeneratorData());
    getAdditionalData();

    return () => {
      dispatch(clearReportState());
    };
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAdditionalData = async () => {
    await dispatch(getMembersList(activeWorkspaceUuid));
    setDownloadingAdditionalData(false);
  };

  useEffect(() => {
    if (downloadingData || downloadingAdditionalData) return;

    const layoutWithoutSeparators =
      layout?.filter((item) => !item.i.includes("separator")) ?? [];

    const uniqueGridElementsLabels = new Set(
      gridElements.map((el) => el.label),
    );
    const missingElements = layoutWithoutSeparators
      .filter((item) => {
        if (item.i !== "cover-box") {
          return !uniqueGridElementsLabels.has(item.i);
        }
        return false;
      })
      .map((el) => el.i);

    const layoutWithoutSeparatorsAndMissingElements =
      layoutWithoutSeparators.filter(
        (item) => !missingElements.includes(item.i),
      );

    if (missingElements.length > 0) {
      console.warn("Missing elements:", missingElements);
    }

    const filteredLayoutLength =
      layoutWithoutSeparatorsAndMissingElements.length - 1;

    if (
      gridElements.length === filteredLayoutLength &&
      filteredLayoutLength > 3
    ) {
      if (!imagesLoaded) {
        loadAllImages();
      }

      setTimeout(() => {
        dispatch(setReportLoading(false));
      }, 750);
    }
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridElements, layout, downloadingData, downloadingAdditionalData]);

  return (
    <div
      ref={generatorRef}
      className="report-generator template-generator"
      style={{
        marginTop: notifications.length * 44,
        height: `calc(100vh - ${notifications.length * 44}px)`,
      }}
    >
      <ReportTopBar
        generatorRef={generatorRef}
        format={format}
        wsWorkspaceUuid={activeWorkspaceUuid}
      />

      <ReportToolbar
        showcaseRef={generatorRef}
        format={format}
        coverImage={coverImage}
        setCoverImage={setCoverImage}
      />

      <div className="report-generator__template-wrapper">
        <ReportTemplate
          format={format.value}
          pages={pages}
          setPages={setPages}
          templateApplied={templateApplied}
          setTemplateApplied={setTemplateApplied}
          coverImage={coverImage?.value}
        />
      </div>
    </div>
  );
}
